import { create } from 'zustand';

export interface CapExItem {
  id: string;
  name: string;
  costPerUnit: number;
  isCustom: boolean;
}

export const STANDARD_CAPEX_ITEMS = [
  'Kitchen', 'Bathroom', 'Flooring', 'HVAC', 'Electrical', 'Plumbing', 'Other',
] as const;

export const CUSTOM_CAPEX_OPTIONS = [
  'Structural', 'MEP', 'Roof', 'Exterior', 'Landscaping', 'Windows', 'Insulation', 'Painting',
] as const;

interface AssumptionDraft {
  // Financing
  interestRate: number | null;
  constructionLoanRate: number | null;
  constructionLoanPoints: number | null;
  constructionPeriodMonths: number | null;
  // Rent
  rentPerUnit: number | null;
  vacancyRate: number | null;
  // Growth
  rentGrowth: number | null;
  expenseGrowthRate: number | null;
  // OpEx
  opexRate: number | null;
  // CapEx
  capexItems: CapExItem[];
  contingencyRate: number;
}

export interface BaselineValues {
  interestRate: number;
  constructionLoanRate: number;
  constructionLoanPoints: number;
  constructionPeriodMonths: number;
  rentPerUnit: number;
  vacancyRate: number;
  rentGrowth: number;
  expenseGrowthRate: number;
  opexRate: number;
  capexItems: CapExItem[];
  totalUnits: number;
}

interface AssumptionDraftStore {
  draft: AssumptionDraft;
  baseline: BaselineValues | null;
  isOpen: boolean;
  isModified: boolean;

  // Actions
  openDrawer: (baseline: BaselineValues) => void;
  closeDrawer: () => void;
  setField: <K extends keyof AssumptionDraft>(key: K, value: AssumptionDraft[K]) => void;
  resetField: (key: keyof AssumptionDraft) => void;
  resetAll: () => void;

  // CapEx
  addCapexItem: (name: string, costPerUnit: number) => void;
  updateCapexItem: (id: string, costPerUnit: number) => void;
  removeCapexItem: (id: string) => void;

  // Derived
  getChangedFields: () => string[];
  getOverrides: () => Record<string, unknown>;
  getCapexTotal: (totalUnits: number) => number;
}

const emptyDraft: AssumptionDraft = {
  interestRate: null,
  constructionLoanRate: null,
  constructionLoanPoints: null,
  constructionPeriodMonths: null,
  rentPerUnit: null,
  vacancyRate: null,
  rentGrowth: null,
  expenseGrowthRate: null,
  opexRate: null,
  capexItems: [],
  contingencyRate: 0.10,
};

const SCALAR_KEYS: (keyof AssumptionDraft)[] = [
  'interestRate', 'constructionLoanRate', 'constructionLoanPoints',
  'constructionPeriodMonths', 'rentPerUnit', 'vacancyRate',
  'rentGrowth', 'expenseGrowthRate', 'opexRate',
];

function computeChanged(draft: AssumptionDraft, baseline: BaselineValues): string[] {
  const changed: string[] = [];
  for (const key of SCALAR_KEYS) {
    if (draft[key] !== (baseline as unknown as Record<string, unknown>)[key]) changed.push(key);
  }
  if (JSON.stringify(draft.capexItems) !== JSON.stringify(baseline.capexItems)) {
    changed.push('capexItems');
  }
  return changed;
}

export const useAssumptionDraftStore = create<AssumptionDraftStore>((set, get) => ({
  draft: { ...emptyDraft },
  baseline: null,
  isOpen: false,
  isModified: false,

  openDrawer: (baseline) => set({
    isOpen: true,
    baseline,
    draft: {
      interestRate: baseline.interestRate,
      constructionLoanRate: baseline.constructionLoanRate,
      constructionLoanPoints: baseline.constructionLoanPoints,
      constructionPeriodMonths: baseline.constructionPeriodMonths,
      rentPerUnit: baseline.rentPerUnit,
      vacancyRate: baseline.vacancyRate,
      rentGrowth: baseline.rentGrowth,
      expenseGrowthRate: baseline.expenseGrowthRate,
      opexRate: baseline.opexRate,
      capexItems: [...baseline.capexItems],
      contingencyRate: 0.10,
    },
    isModified: false,
  }),

  closeDrawer: () => set({ isOpen: false }),

  setField: (key, value) => set((state) => {
    const newDraft = { ...state.draft, [key]: value };
    const changed = state.baseline ? computeChanged(newDraft, state.baseline) : [];
    return { draft: newDraft, isModified: changed.length > 0 };
  }),

  resetField: (key) => set((state) => {
    if (!state.baseline) return state;
    const baselineValue = key === 'capexItems'
      ? [...state.baseline.capexItems]
      : (state.baseline as unknown as Record<string, unknown>)[key];
    const newDraft = { ...state.draft, [key]: baselineValue };
    const changed = computeChanged(newDraft, state.baseline);
    return { draft: newDraft, isModified: changed.length > 0 };
  }),

  resetAll: () => set((state) => {
    if (!state.baseline) return state;
    return {
      draft: {
        interestRate: state.baseline.interestRate,
        constructionLoanRate: state.baseline.constructionLoanRate,
        constructionLoanPoints: state.baseline.constructionLoanPoints,
        constructionPeriodMonths: state.baseline.constructionPeriodMonths,
        rentPerUnit: state.baseline.rentPerUnit,
        vacancyRate: state.baseline.vacancyRate,
        rentGrowth: state.baseline.rentGrowth,
        expenseGrowthRate: state.baseline.expenseGrowthRate,
        opexRate: state.baseline.opexRate,
        capexItems: [...state.baseline.capexItems],
        contingencyRate: 0.10,
      },
      isModified: false,
    };
  }),

  addCapexItem: (name, costPerUnit) => set((state) => {
    const newDraft = {
      ...state.draft,
      capexItems: [
        ...state.draft.capexItems,
        { id: `custom-${Date.now()}`, name, costPerUnit, isCustom: true },
      ],
    };
    return { draft: newDraft, isModified: true };
  }),

  updateCapexItem: (id, costPerUnit) => set((state) => {
    const newDraft = {
      ...state.draft,
      capexItems: state.draft.capexItems.map((item) =>
        item.id === id ? { ...item, costPerUnit } : item
      ),
    };
    const changed = state.baseline ? computeChanged(newDraft, state.baseline) : [];
    return { draft: newDraft, isModified: changed.length > 0 };
  }),

  removeCapexItem: (id) => set((state) => {
    const newDraft = {
      ...state.draft,
      capexItems: state.draft.capexItems.filter((item) => item.id !== id),
    };
    const changed = state.baseline ? computeChanged(newDraft, state.baseline) : [];
    return { draft: newDraft, isModified: changed.length > 0 };
  }),

  getChangedFields: () => {
    const { draft, baseline } = get();
    if (!baseline) return [];
    return computeChanged(draft, baseline);
  },

  getOverrides: () => {
    const { draft, baseline } = get();
    if (!baseline) return {};
    const overrides: Record<string, unknown> = {};
    if (draft.interestRate !== baseline.interestRate) overrides.interest_rate = draft.interestRate;
    if (draft.constructionLoanRate !== baseline.constructionLoanRate) overrides.construction_loan_rate = draft.constructionLoanRate;
    if (draft.constructionLoanPoints !== baseline.constructionLoanPoints) overrides.construction_loan_points = draft.constructionLoanPoints;
    if (draft.constructionPeriodMonths !== baseline.constructionPeriodMonths) overrides.construction_period_months = draft.constructionPeriodMonths;
    if (draft.rentPerUnit !== baseline.rentPerUnit) overrides.rent_per_unit = draft.rentPerUnit;
    if (draft.vacancyRate !== baseline.vacancyRate) overrides.vacancy = draft.vacancyRate;
    if (draft.rentGrowth !== baseline.rentGrowth) overrides.rent_growth = draft.rentGrowth;
    if (draft.expenseGrowthRate !== baseline.expenseGrowthRate) overrides.expense_growth_rate = draft.expenseGrowthRate;
    if (draft.opexRate !== baseline.opexRate) overrides.opex_rate = draft.opexRate;
    if (JSON.stringify(draft.capexItems) !== JSON.stringify(baseline.capexItems)) {
      const totalUnits = baseline.totalUnits || 1;
      const subtotal = draft.capexItems.reduce((sum, item) => sum + item.costPerUnit * totalUnits, 0);
      const contingency = subtotal * draft.contingencyRate;
      overrides.capex_total = subtotal + contingency;
      overrides.capex_items = draft.capexItems.map(item => ({
        name: item.name,
        cost_per_unit: item.costPerUnit,
        is_custom: item.isCustom,
      }));
    }
    return overrides;
  },

  getCapexTotal: (totalUnits) => {
    const { draft } = get();
    const subtotal = draft.capexItems.reduce((sum, item) => sum + item.costPerUnit * totalUnits, 0);
    return subtotal + subtotal * draft.contingencyRate;
  },
}));
